import { cva, type VariantProps } from 'class-variance-authority';

/**
 * EditorialListing — layout and chrome backed by HEN design tokens (tailwind.config.ts).
 * Variants live here and are consumed by the composite; the root `Card` is not `className`-mergeable
 * so styling stays token-driven.
 */

/** Card root: padding; mobile `gap-md`; desktop `gap-0` so separator track supplies horizontal rhythm via `px-md`. */
export const editorialListingRootVariants = cva(
  'flex w-full min-w-0 flex-col gap-md p-md md:flex-row md:items-stretch md:gap-0',
);

/**
 * Mobile-only logo row — `self-start` avoids the card column’s default cross-axis stretch and
 * left-aligns the fixed-width logo; pair with `<Flex direction="column" align="start" />`.
 */
export const editorialListingMobileLogoRowVariants = cva(
  'self-start md:hidden',
);

/** Shrink/grow: pair with `<Flex direction="column" gap="sm" />`. */
export const editorialListingContentColumnVariants = cva('min-w-0 flex-1');

/** Badge in tag list — no wrap inside pill. */
export const editorialListingTagBadgeVariants = cva('whitespace-nowrap');

/**
 * Logo shell — fixed 108×62 outer box. Inset `p-xxs` (8px via `--space-xxs`).
 * Inner image uses `object-fit: contain` so mixed logo aspect ratios render fully without cropping.
 */
export const editorialListingLogoShellVariants = cva(
  'box-border flex min-h-0 min-w-0 shrink-0 overflow-hidden bg-white rounded-sm p-xxs',
  {
    variants: {
      layout: {
        mobile: 'h-editorial-listing-logo w-editorial-listing-logo shadow-md',
        desktop: 'h-editorial-listing-logo w-editorial-listing-logo shadow-xl',
      },
    },
    defaultVariants: {
      layout: 'desktop',
    },
  },
);

/** Logo image — fills padded shell; `object-fit: contain` on `<Image />` keeps the full logo visible. */
export const editorialListingLogoImageVariants = cva(
  'h-full min-h-0 w-full min-w-0 flex-1 basis-0',
);

/**
 * Desktop-only track: horizontal flex + `items-stretch` so the line gets a real cross-axis
 * height from the row (avoids `h-full` / `flex-1` collapse when the parent height is content-sized).
 * `px-md` is horizontal space on each side of the 1px rule.
 */
export const editorialListingSeparatorTrackVariants = cva(
  'hidden md:flex md:min-h-0 md:shrink-0 md:flex-row md:items-stretch md:self-stretch md:px-md',
);

/**
 * Vertical rule: `h-auto` + `self-stretch` so flex gives a real height (Shadcn’s `h-full` often
 * collapses here). Stroke matches Separator default (`neutral-100`).
 */
export const editorialListingSeparatorLineVariants = cva(
  'h-auto min-h-0 w-px shrink-0 self-stretch bg-neutral-100',
);

/** Branding column: logo + Visit Site (desktop). */
export const editorialListingBrandingColumnVariants = cva(
  'hidden w-full shrink-0 flex-col items-center justify-center gap-sm md:flex md:w-editorial-listing-branding',
);

/** Visit Site slot under content on mobile. */
export const editorialListingMobileVisitVariants = cva('w-full md:hidden');

/** Read-more expand toggle (Button textOnly). */
export const editorialListingReadMoreToggleVariants = cva(
  'h-auto self-start font-bold',
);

/** Read-more outbound link. */
export const editorialListingReadMoreLinkVariants = cva('self-start font-bold');

/** Visit Site primary button: full width on mobile only. */
export const editorialListingVisitButtonVariants = cva('', {
  variants: {
    width: {
      auto: '',
      full: 'w-full justify-center',
    },
  },
  defaultVariants: {
    width: 'auto',
  },
});

export type EditorialListingLogoShellVariantProps = VariantProps<
  typeof editorialListingLogoShellVariants
>;
export type EditorialListingVisitButtonVariantProps = VariantProps<
  typeof editorialListingVisitButtonVariants
>;
